import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { extractErrorTk } from "@/utils/errorUtils";
import type { PendingInvitation } from "@/types/sharing";
import { useInvitations } from "@/hooks/useInvitations";
import { INVITATION_KEYS } from "@/i18n/sharing.keys";

function formatExpiryShort(expiresAt: string): {
  text: string;
  isUrgent: boolean;
} {
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  if (remainingMs <= 0) return { text: "Expired", isUrgent: true };
  const days = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  if (days > 1) return { text: `${days}d left`, isUrgent: days <= 2 };
  const hours = Math.ceil(remainingMs / (60 * 60 * 1000));
  return { text: `${hours}h left`, isUrgent: true };
}

export const InvitationBanner: React.FC = () => {
  const { invitations, acceptInvitation, declineInvitation } = useInvitations();
  const { t } = useTranslation();

  const handleAccept = useCallback(
    async (invitation: PendingInvitation) => {
      try {
        await acceptInvitation(invitation.invitationId);
        toast.success(
          invitation.type === "team"
            ? t(INVITATION_KEYS.joinedTeam, {
                teamName: invitation.teamName,
                role: invitation.role,
              })
            : t(INVITATION_KEYS.joinedProject, {
                projectName: invitation.projectName,
                role: invitation.role,
              }),
        );
      } catch (error) {
        const tk = extractErrorTk(error);
        toast.error(tk ? t(tk) : t("errors.generic"));
      }
    },
    [acceptInvitation, t],
  );

  const handleDecline = useCallback(
    async (invitation: PendingInvitation) => {
      try {
        await declineInvitation(invitation.invitationId);
        toast.info(
          invitation.type === "team"
            ? t(INVITATION_KEYS.declinedTeamInvite, {
                teamName: invitation.teamName,
              })
            : t(INVITATION_KEYS.declinedInvite, {
                projectName: invitation.projectName,
              }),
        );
      } catch (error) {
        const tk = extractErrorTk(error);
        toast.error(tk ? t(tk) : t("errors.generic"));
      }
    },
    [declineInvitation, t],
  );

  if (invitations.length === 0) return null;

  return (
    <div className="px-8 py-4 space-y-2 bg-[var(--color-primitive-neutral-800)] border-b border-white/10">
      {invitations.map((invitation) => {
        const expiry = formatExpiryShort(invitation.expiresAt);
        return (
          <div
            key={invitation.invitationId}
            className="flex items-center gap-4 px-5 py-4 rounded-[var(--radius-default)] border border-primary/20 bg-primary/10"
          >
            {/* Icon */}
            <Mail className="w-5 h-5 text-primary shrink-0" />

            {/* Role badge and expiry */}
            <div className="flex items-center gap-3 shrink-0">
              <Badge variant="secondary" className="text-xs">
                {invitation.role}
              </Badge>
              <span
                className={`text-xs flex items-center gap-1 ${expiry.isUrgent ? "text-destructive font-medium" : "text-muted-foreground"}`}
              >
                <Clock className="w-3 h-3" />
                {expiry.text}
              </span>
            </div>

            {/* Invitation text - centered */}
            <p className="flex-1 text-sm text-center text-muted-foreground">
              <span className="font-medium text-foreground">
                {invitation.invitedByName}
              </span>{" "}
              {invitation.type === "team" ? (
                <>
                  {t(INVITATION_KEYS.invitedYouToJoin)}{" "}
                  <span className="font-medium text-foreground">
                    {invitation.teamName}
                  </span>
                </>
              ) : (
                <>
                  {t(INVITATION_KEYS.invitedYouTo)}{" "}
                  <span className="font-medium text-foreground">
                    {invitation.projectName}
                  </span>
                </>
              )}
            </p>

            {/* Action buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="pink"
                onClick={() => handleAccept(invitation)}
                className="gap-1.5"
              >
                <Check className="w-3.5 h-3.5" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="grey"
                onClick={() => handleDecline(invitation)}
                className="gap-1.5"
              >
                <X className="w-3.5 h-3.5" />
                Decline
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
